import { IconButton, LuIcon, Menu, stopPropagation } from "@overmap-ai/blocks"
import {
	ImageCard as OvermapImageCard,
	type ImageCardProps as OvermapImageCardProps,
	useImageViewer,
} from "@overmap-ai/forms"
import { saveAs } from "file-saver"
import { memo, type MouseEventHandler, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"

export interface ImageCardProps extends Omit<OvermapImageCardProps, "rightSlot"> {
	onDelete?: (file: File | null) => void
}

export const ImageCard = memo((props: ImageCardProps) => {
	const { file, error, onDelete, ...rest } = props
	const openImageViewer = useImageViewer()

	const handleView = useCallback(() => {
		if (!file) return

		openImageViewer((closeFileViewer) => ({
			file: file,
			onClose: closeFileViewer,
		}))
	}, [file, openImageViewer])

	const handleClick = useCallback<MouseEventHandler>(
		(e) => {
			e.stopPropagation()
			handleView()
		},
		[handleView],
	)

	const handleDownload = useCallback(() => {
		if (!file) return

		saveAs(file, file.name)
	}, [file])

	const handleDelete = useCallback(() => {
		onDelete?.(file)
	}, [file, onDelete])

	return (
		<OvermapImageCard
			onClick={handleClick}
			file={file}
			error={error}
			size="sm"
			rightSlot={
				<div className="flex grow justify-end">
					<Menu.Root>
						<Menu.ClickTrigger>
							<IconButton variant="ghost" size="sm" accentColor="base" onClick={stopPropagation}>
								<LuIcon icon={modelIconMapping.more} />
							</IconButton>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.Item onSelect={handleView}>
								<LuIcon icon={modelIconMapping.expand} />
								View
							</Menu.Item>
							<Menu.Item onSelect={handleDownload} disabled={!file}>
								<LuIcon icon={modelIconMapping.download} />
								Download
							</Menu.Item>

							{onDelete ? (
								<>
									<Menu.Separator />
									<Menu.Item onSelect={handleDelete} disabled={!file}>
										<LuIcon icon={modelIconMapping.delete} />
										Delete
									</Menu.Item>
								</>
							) : null}
						</Menu.Content>
					</Menu.Root>
				</div>
			}
			{...rest}
		/>
	)
})
ImageCard.displayName = "ImageCard"
